import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

export default function InstructorForm({ instructor, mode }) {
  const [name, setName] = useState(instructor.name || '');
  const [email, setEmail] = useState(instructor.email || '');
  const [bioSlide, setBioSlide] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === 'edit';
  const existingBioName = instructor.bio_slide_filename;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    // FormData is required whenever a file might be attached. On edit we POST
    // with _method=patch so multipart survives (Inertia's router.patch sends
    // JSON, which would strip the file).
    const formData = new FormData();
    if (isEdit) formData.append('_method', 'patch');
    formData.append('instructor[name]', name.trim());
    formData.append('instructor[email]', email.trim());
    if (bioSlide) formData.append('instructor[bio_slide]', bioSlide);

    const url = isEdit ? `/superadmin/instructors/${instructor.id}` : '/superadmin/instructors';
    router.post(url, formData, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
      <div>
        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">Name</label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          placeholder="e.g. Jane Smith"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="e.g. jane@example.com"
          className={inputClass}
        />
        <p className="mt-1 text-xs text-gray-500">Must be unique.</p>
      </div>

      <div>
        <label htmlFor="bio_slide" className="block text-sm font-semibold text-gray-700 mb-1.5">
          Bio slide (PDF)
        </label>
        <input
          type="file"
          id="bio_slide"
          accept="application/pdf"
          onChange={(e) => setBioSlide(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
        />
        <p className="mt-1 text-xs text-gray-500">
          Single-page PDF shown as the first slide on every webinar this instructor hosts. Max 10 MB.
        </p>
        {existingBioName && !bioSlide && (
          <p className="mt-2 text-xs text-gray-600">
            Current: <span className="font-medium">{existingBioName}</span>
            {instructor.bio_slide_ready === false && (
              <span className="ml-2 text-amber-600">(still processing…)</span>
            )}
          </p>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
        <Link
          href="/superadmin/instructors"
          className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
        >
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create instructor'}
        </button>
      </div>
    </form>
  );
}
